import type { LabelPreset } from "../labels";
import { getSheetByCode, inchesToPoints, type SheetTemplate } from "../layout/sheetTemplates";
import { computePageCellRects, fitBoxCentered, shouldRotateOnSheet } from "../layout/composeSheet";
import { type FrameKey } from "../frames";

export type CodeItem = { name: string; svg: string };

export type PdfSheetOptions = {
  preset: LabelPreset;
  innerPaddingPct: number; // recommended 0.02..0.08
  startPosition: number; // 1-based index
  includeCaption?: boolean;
  frameKey?: FrameKey;
  frameColor?: string;
  onProgress?: (page: number, totalPages: number) => void;
};

export async function makePdfSheet(codes: CodeItem[], opts: PdfSheetOptions): Promise<Uint8Array> {
  // Lazy load pdf-lib
  const pdfLib: any = await import("pdf-lib");
  const { PDFDocument, StandardFonts, degrees, pushGraphicsState, popGraphicsState, translate, rotateRadians } = pdfLib;

  const sheet: SheetTemplate = getSheetByCode(opts.preset.code);
  const rotate = shouldRotateOnSheet(opts.preset, sheet);

  const pageW = inchesToPoints(sheet.pageSizeIn.w);
  const pageH = inchesToPoints(sheet.pageSizeIn.h);

  const perPage = sheet.columns * sheet.rows;
  const startPos = Math.max(1, Math.floor(opts.startPosition || 1));
  const firstPageAvail = perPage - ((startPos - 1) % perPage);
  const remaining = Math.max(0, codes.length - firstPageAvail);
  const extraPages = remaining > 0 ? Math.ceil(remaining / perPage) : 0;
  const totalPages = codes.length === 0 ? 1 : 1 + extraPages;

  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);

  // Prepare artwork PNGs by rasterizing the final composed SVGs
  const pngDataURLs: string[] = [];
  const pngSizes: { w: number; h: number }[] = [];
  for (const c of codes) {
    const minIn = Math.min(sheet.labelSizeIn.w, sheet.labelSizeIn.h);
    const px = Math.max(300, Math.round(minIn * 300));
    const canvas = await svgToCanvas(c.svg, px);
    pngDataURLs.push(canvas.toDataURL("image/png"));
    pngSizes.push({ w: canvas.width, h: canvas.height });
    await new Promise((r) => setTimeout(r, 0));
  }

  const cellRects = computePageCellRects(sheet);

  let index = 0;
  let globalCell = 1;
  let pageNo = 0;
  while (index < codes.length) {
    const page = doc.addPage([pageW, pageH]);
    pageNo++;
    if (opts.onProgress) opts.onProgress(pageNo, totalPages);

    for (let cellIdx = 0; cellIdx < cellRects.length; cellIdx++) {
      const thisCellNumber = (pageNo - 1) * perPage + (cellIdx + 1);
      if (thisCellNumber < startPos) {
        globalCell++;
        continue;
      }
      if (index >= codes.length) {
        globalCell++;
        continue;
      }

      const cell = cellRects[cellIdx];
      const pngUrl = pngDataURLs[index];
      const natSize = pngSizes[index];
      const code = codes[index];
      index++;
      globalCell++;

      // Determine artwork aspect ratio from the prepared PNG dimensions
      const nat = natSize || { w: 1000, h: 1000 };
      const aspect = rotate ? (nat.h / nat.w) : (nat.w / nat.h);
      const minPad = opts.preset.kind === "framed" ? 0.06 : 0.04; // add safety margin for framed
      const box = fitBoxCentered(cell, Math.max(minPad, opts.innerPaddingPct), aspect);

      const img = await doc.embedPng(pngUrl);

      if (rotate) {
        const cx = box.x + box.w / 2;
        const cy = box.y + box.h / 2;
        if (pushGraphicsState && popGraphicsState && translate && rotateRadians) {
          const rad = (-90 * Math.PI) / 180;
          page.pushOperators(pushGraphicsState());
          page.pushOperators(translate(cx, cy));
          page.pushOperators(rotateRadians(rad));
          // After rotation, the drawn image's extents are swapped; draw with swapped width/height
          const drawW = box.h;
          const drawH = box.w;
          page.drawImage(img, { x: -drawW / 2, y: -drawH / 2, width: drawW, height: drawH });
          page.pushOperators(popGraphicsState());
        } else {
          // Fallback: use drawImage rotation around lower-left; approximate centering
          page.drawImage(img, { x: box.x, y: box.y, width: box.h, height: box.w, rotate: degrees(-90) });
        }
      } else {
        page.drawImage(img, { x: box.x, y: box.y, width: box.w, height: box.h });
      }

      if (opts.includeCaption) {
        const text = code.name.replace(/\.[a-z0-9]+$/i, "");
        const fontSize = 8.5;
        const textWidth = font.widthOfTextAtSize(text, fontSize);
        const tx = cell.x + (cell.w - textWidth) / 2;
        const ty = cell.y + 4;
        page.drawText(text, { x: tx, y: ty, size: fontSize, font });
      }
    }
  }

  return await doc.save();
}

// Utilities (local, minimal)
async function svgToCanvas(svgString: string, px = 1024): Promise<HTMLCanvasElement> {
  const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const img = await loadImage(url);
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = Math.round(px * (img.height / Math.max(1, img.width)));
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  try { URL.revokeObjectURL(url); } catch {}
  return canvas;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error("Failed to load image"));
    img.src = url;
  });
}


